import { HandDisplay } from '../problems/HandDisplay';
import { SeatBoardLayout } from '../table-session/SeatBoardLayout';
import { SeatLabel } from '../table-session/SeatLabel';
import { VulnerabilityBoard } from '../table-session/VulnerabilityBoard';
import { compassAxis, splitHands, type CompassSeat } from './conceptualHands';
import { CompassBoardLayout } from './CompassBoardLayout';
import type { ConceptualProblem, SeatHand } from '../../types/conceptualProblems';

/**
 * The frame's hand panel for a conceptual problem (feature 029).
 *
 * <p><b>Why every recorded hand, and not just the deciding seat's.</b> The practice pages show one
 * hand because the learner holds one hand. This page is not asking anyone to bid: it shows an
 * editor how a problem is put together, and 20 of the 823 problems record a hand only for a seat
 * OTHER than the one being asked. Under the practice rule those hands would simply vanish, and an
 * editor would be judging a problem they could not fully see.</p>
 *
 * <p><b>Two hands sit round the board by compass seat</b> (client, 2026-10-05): North above the
 * board and South below it, West left of it and East right of it, as the Counting page places its
 * seats (`CompassBoardLayout`). Until then only the first hand was placed by seat and the second
 * followed in a wrapping row — east of the board, or under it beside the first. A lone hand keeps
 * the shared `SeatBoardLayout`, exactly as the practice pages draw it. The height a North/South
 * stack takes is given back by the page's own layout: the hand panel runs the full height beside
 * the answer (styles/data-editing.css).</p>
 *
 * <p><b>Both hands live in ONE panel.</b> The obvious alternative was the frame's
 * `secondaryHand` slot, which is what the feature 025 review surface uses for partner's hand — but
 * that surface is not viewport-locked and this one is, and the measurements say a fourth column
 * does not survive the locked bands. At 754x303 the landscape row's grid is three columns by
 * construction, so a fourth panel squeezed the auction to 51px and pushed the question and the
 * answer onto rows of their own; at 360x640 the extra stacked panel put the answer 41px BELOW the
 * frame, which clips it. Two hands in one panel leave the three-panel row exactly as every band
 * was measured for.</p>
 *
 * <p><b>The panel is always drawn</b> (client decision, 2026-09-09), even for the roughly nine in
 * ten problems that record no hand at all: it then says so in words. An editor stepping through a
 * category reads a row whose columns never move, and "no hand" is stated rather than left to be
 * inferred from an absence.</p>
 */
export function ConceptualHandPanel({ problem }: { problem: ConceptualProblem }) {
  const { first, second } = splitHands(problem.hands);
  // The board follows the hand when there is one, and the seat being asked otherwise, so the
  // arrangement always reads as a picture of the table rather than defaulting to South.
  const seat = first?.seat ?? problem.decisionSeat;
  const hasVulnerability = problem.vulnerability.trim().length > 0;

  const board = hasVulnerability ? (
    <VulnerabilityBoard
      vulnerability={problem.vulnerability}
      decisionSeat={seat === '' ? undefined : seat}
      dealer={problem.auction[0]?.seat}
    />
  ) : null;

  // The 47 problems that record two hands: both placed by seat round the board.
  if (first && second && isCompassSeat(first.seat) && isCompassSeat(second.seat)) {
    const eastWest = compassAxis([first.seat, second.seat]) === 'ew';
    return (
      <div className="flex justify-center" data-testid="conceptual-hands">
        <CompassBoardLayout
          headingClassName={SEAT_HEADING_CLASS}
          board={board}
          hands={[first, second].map((h) => ({
            seat: h.seat as CompassSeat,
            label: <SeatHeading seatHand={h} compass stackAsked={eastWest} />,
            hand: <HandDisplay hand={h.hand} />,
            testId: `conceptual-hand-${h.seat}`,
          }))}
        />
      </div>
    );
  }

  return (
    <div
      className="flex flex-wrap items-start justify-center gap-3 xnarrow:gap-1.5"
      data-testid="conceptual-hands"
    >
      <SeatBoardLayout
        seat={seat}
        handLabel={first ? <SeatHeading seatHand={first} /> : null}
        hand={
          first ? (
            <div data-testid={`conceptual-hand-${first.seat}`}>
              <HandDisplay hand={first.hand} />
            </div>
          ) : (
            // Stated, never implied. Nine problems in ten reach this branch, and an empty column
            // would read as a hand that failed to load.
            <p className="text-[14px] text-ink-soft" data-testid="conceptual-no-hand">
              No hand given for this problem.
            </p>
          )
        }
        board={board}
      />
    </div>
  );
}

function isCompassSeat(seat: string): seat is CompassSeat {
  return seat === 'N' || seat === 'E' || seat === 'S' || seat === 'W';
}

/** Matches `PanelHeading` and `SeatBoardLayout` exactly — 12px floor, sentence case on phones. */
const SEAT_HEADING_CLASS =
  'mb-2.5 text-xs font-semibold uppercase tracking-[0.09em] text-ink-soft narrow:normal-case narrow:tracking-normal padswap:mb-1.5 xnarrow:mb-1';

/**
 * "West holds", plus the marker naming the seat the question is actually put to.
 *
 * `compass`: the two-hand layout. On a portrait tablet the hand column is sized to its content and
 * an East/West pair sits in one row with the board, so the headings are what widen it — at 768 wide
 * "EAST HOLDS (ASKED)" left the auction ~120px and its calls ran into each other. There the seat is
 * a letter (as HandPanel does on the practice pages) and "(asked)" takes its own line.
 *
 * `stackAsked`: an East/West pair, where width is what is scarce on every band — three columns
 * (West, the board, East) share the hand column — so "(asked)" takes its own line everywhere
 * rather than making "EAST HOLDS (ASKED)" the widest thing in the column (client, 2026-10-05).
 */
function SeatHeading({
  seatHand,
  compass = false,
  stackAsked = false,
}: {
  seatHand: SeatHand;
  compass?: boolean;
  stackAsked?: boolean;
}) {
  const askedLine = stackAsked ? ' block' : compass ? ' tablet-pt:block' : '';
  return (
    <>
      <SeatLabel seat={seatHand.seat} letterOnTabletPortrait={compass} /> holds
      {seatHand.isDecisionSeat && (
        <span className={`font-normal text-ink-soft${askedLine}`}> (asked)</span>
      )}
    </>
  );
}
